// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

type ChainsSortBy = components["schemas"]["ChainsSortByEnum"];
type ChainsSettingsSortBy = components["schemas"]["ChainsSettingsSortByEnum"];
type SortOrder = components["schemas"]["HttpSortingOrderEnum"];

interface ChainsListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: ChainsSortBy;
  orderBy?: SortOrder;
  onlyActive?: boolean;
}

export function useChainsQuery(params: ChainsListParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, onlyActive } = params;

  return useQuery({
    queryKey: ["chains", "list", { page, limit, search, sortBy, orderBy, onlyActive }],
    queryFn: async () => {
      const res = await api.GET("/v1/chains", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[onlyActive]": onlyActive,
          },
        },
      });
      return res.data as unknown as {
        chains: Array<{
          chainId: number;
          chainName: string;
          legibleName: string;
          defaultTimeZone: string;
          storesCount: number;
          activeStoresCount: number;
          isActive: boolean;
        }>;
        count: number;
      };
    },
  });
}

export function useChainDetailQuery(chainName: string) {
  return useQuery({
    queryKey: ["chains", "detail", chainName],
    queryFn: async () => {
      const res = await api.GET("/v1/chains/{chain_name}", {
        params: { path: { chain_name: chainName } },
      });
      return res.data as unknown as {
        chainId: number;
        chainName: string;
        legibleName: string;
        defaultTimeZone: string;
        storesCount: number;
        activeStoresCount: number;
        isActive: boolean;
        createdAt?: string;
        updatedAt?: string;
      };
    },
    enabled: !!chainName,
  });
}

export function useCreateChainMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      chainName: string;
      legibleName: string;
      defaultTimeZone: string;
      copyFrom?: string;
    }) => {
      const { copyFrom, ...body } = params;
      const res = await api.POST("/v1/chains", {
        params: { query: { copyFrom: copyFrom || undefined } },
        body,
      });
      if (res.error) throw new Error("Failed to create chain");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chains"] });
    },
  });
}

export function useEditChainMutation(chainName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { chainName: string; legibleName: string; defaultTimeZone: string }) => {
      const res = await api.PATCH("/v1/chains/{chain_name}", {
        params: { path: { chain_name: chainName } },
        body,
      });
      if (res.error) throw new Error("Failed to update chain");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chains"] });
    },
  });
}

// --- Regions ---

export function useChainRegionsQuery(chainName: string) {
  return useQuery({
    queryKey: ["chains", "regions", chainName],
    queryFn: async () => {
      const res = await api.GET("/v1/chains/{chain_name}/regions", {
        params: { path: { chain_name: chainName } },
      });
      return res.data as unknown as {
        regions: Array<{ regionName: string; storesCount: number }>;
        count: number;
      };
    },
    enabled: !!chainName,
  });
}

export function useEditChainRegionMutation(chainName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { oldRegionName: string; newRegionName: string }) => {
      const res = await api.PATCH("/v1/chains/{chain_name}/regions", {
        params: { path: { chain_name: chainName } },
        body: [body],
      });
      if (res.error) throw new Error("Failed to update");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chains", "regions", chainName],
      });
    },
  });
}

export function useDeleteChainRegionMutation(chainName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (regionName: string) => {
      const res = await api.DELETE("/v1/chains/{chain_name}/regions", {
        params: { path: { chain_name: chainName } },
        body: [{ regionName }],
      });
      if (res.error) throw new Error("Failed to delete");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chains", "regions", chainName],
      });
    },
  });
}

// --- Settings ---

export interface ChainSettingEntry {
  settingId: number;
  key: string;
  name: string;
  description: string;
  groupName: string;
  fieldType: "boolean" | "number" | "string" | "list" | "json" | "enum";
  enumValues?: string[];
  defaultValue: string;
  overrideId?: number;
  overrideValue?: string;
  storeOverridesCount: number;
}

interface ChainSettingsListParams {
  chainName: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: ChainsSettingsSortBy;
  orderBy?: SortOrder;
  group?: string;
  onlyOverrides?: boolean;
}

export function useChainSettingsQuery(params: ChainSettingsListParams) {
  const { chainName, page = 1, limit = 20, search, sortBy, orderBy, group, onlyOverrides } = params;

  return useQuery({
    queryKey: ["chains", "settings", chainName, { page, limit, search, sortBy, orderBy, group, onlyOverrides }],
    queryFn: async () => {
      const res = await api.GET("/v1/chains/{chain_name}/settings", {
        params: {
          path: { chain_name: chainName },
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[group]": group || undefined,
            "filter[onlyOverrides]": onlyOverrides,
          },
        },
      });
      return res.data as unknown as {
        settings: ChainSettingEntry[];
        count: number;
      };
    },
    enabled: !!chainName,
  });
}

export function useAddChainSettingMutation(chainName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { settingId: number; newValue: string }) => {
      const res = await api.POST("/v1/chains/{chain_name}/settings", {
        params: { path: { chain_name: chainName } },
        body,
      });
      if (res.error) throw new Error("Failed to add");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chains", "settings", chainName],
      });
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useEditChainSettingMutation(chainName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { overrideId: number; newValue: string }) => {
      const res = await api.PATCH("/v1/chains/{chain_name}/settings", {
        params: { path: { chain_name: chainName } },
        body: [body],
      });
      if (res.error) throw new Error("Failed to update");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chains", "settings", chainName],
      });
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useDeleteChainSettingMutation(chainName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (overrideId: number) => {
      const res = await api.DELETE("/v1/chains/{chain_name}/settings", {
        params: { path: { chain_name: chainName } },
        body: [{ overrideId }],
      });
      if (res.error) throw new Error("Failed to delete");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chains", "settings", chainName],
      });
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}
